import type { CurrentPeriodPosition } from '@localagent/sdk';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCredits(credits: number): string {
  if (credits >= 1_000_000) {
    return `${(credits / 1_000_000).toFixed(1)}M`;
  }
  if (credits >= 1_000) {
    return `${(credits / 1_000).toFixed(1)}K`;
  }
  return credits.toLocaleString();
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const statusColors: Record<string, string> = {
  open: 'bg-green-500/10 text-green-700 dark:text-green-400',
  awaiting_payment: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
  settling: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  settled: 'bg-muted text-muted-foreground',
  settlement_failed: 'bg-red-500/10 text-red-700 dark:text-red-400',
  opening: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  open_failed: 'bg-red-500/10 text-red-700 dark:text-red-400',
};

export interface CurrentPeriodCardProps {
  position: CurrentPeriodPosition;
}

export function CurrentPeriodCard({ position }: CurrentPeriodCardProps) {
  const {
    included_credits,
    credits_consumed,
    credits_remaining,
    overage_credits,
    overage_amount_cents,
    advance_amount_cents,
    period_end,
    status,
    seat_count,
    never_bill,
  } = position;

  // Advance decline: credits are not granted until the period opens.
  const creditsGranted = status !== 'awaiting_payment' && status !== 'opening';
  const displayIncluded = creditsGranted ? included_credits : 0;
  const displayRemaining = creditsGranted ? credits_remaining : 0;
  const displayConsumed = creditsGranted ? credits_consumed : 0;

  const pct =
    displayIncluded > 0
      ? Math.round((displayRemaining / displayIncluded) * 100)
      : 0;
  const consumedPct =
    displayIncluded > 0
      ? Math.min(100, Math.round((displayConsumed / displayIncluded) * 100))
      : 0;

  const now = new Date();
  const end = new Date(period_end);
  const daysRemaining = Math.max(
    0,
    Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
  );

  const statusClass = statusColors[status] ?? 'bg-muted text-muted-foreground';

  return (
    <div className="rounded-lg bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">
            Current Period
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span
              className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${statusClass}`}
            >
              {status.replace(/_/g, ' ')}
            </span>
            {seat_count > 1 && (
              <span className="text-xs text-muted-foreground">
                {seat_count} seats
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          {never_bill ? (
            <span
              className="inline-block rounded bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-400"
              data-testid="billing-period-complimentary"
            >
              Complimentary
            </span>
          ) : (
            <p
              className="text-lg font-semibold"
              data-testid="billing-period-advance"
            >
              {formatCents(advance_amount_cents)}/mo
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-xs text-muted-foreground">Allowance</span>
          <p
            className="text-lg font-semibold"
            data-testid="billing-period-allowance"
          >
            {creditsGranted ? formatCredits(displayIncluded) : 'Pending payment'}
          </p>
        </div>
        <div>
          <span className="text-xs text-muted-foreground">Consumed</span>
          <p
            className="text-lg font-semibold"
            data-testid="billing-period-consumed"
          >
            {formatCredits(displayConsumed)}
          </p>
        </div>
      </div>

      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            pct > 50 ? 'bg-green-500' : pct > 10 ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${consumedPct}%` }}
        />
      </div>

      <div className="flex justify-between text-sm">
        <span data-testid="billing-period-remaining">
          {formatCredits(displayRemaining)} remaining ({pct}%)
        </span>
        {creditsGranted && overage_credits > 0 && (
          <span
            className="text-yellow-700 dark:text-yellow-400"
            data-testid="billing-period-overage"
          >
            {formatCredits(overage_credits)} overage ({formatCents(overage_amount_cents)})
          </span>
        )}
      </div>

      <div
        className="text-xs text-muted-foreground"
        data-testid="billing-period-next-charge"
      >
        {daysRemaining} days remaining · Next charge {formatDate(period_end)}
      </div>
    </div>
  );
}
